import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getCurrentNudge, markNudgeSeen, dismissNudge } from '../actions/api';

const POLL_MS = 15000;
const MIN_SHOW_MS = 60000;      // always visible at least this long
const AFTER_ACTIVE_MS = 10000;  // then leaves this long after the rep is active
const EXIT_MS = 450;            // matches the slide-out animation
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'];

// A pit stop nudge from Race control. Shows on any page; stays up at least 60 seconds and
// until the rep is active (mouse, keys, scroll, touch), then slides away 10 seconds later.
const NudgeToast = () => {
  const [nudge, setNudge] = useState(null);
  const [leaving, setLeaving] = useState(false);
  const shownAt = useRef(0);
  const activeAt = useRef(0);
  const currentId = useRef(null);
  const closing = useRef(false);

  const close = useCallback(() => {
    if (!currentId.current || closing.current) return;
    closing.current = true;
    const id = currentId.current;
    setLeaving(true);
    dismissNudge(id).catch(() => {});
    setTimeout(() => {
      currentId.current = null;
      closing.current = false;
      setLeaving(false);
      setNudge(null);
    }, EXIT_MS);
  }, []);

  // Look for new nudges.
  useEffect(() => {
    let alive = true;
    const poll = () => getCurrentNudge().then((n) => {
      if (!alive || !n || n.id === currentId.current || closing.current) return;
      currentId.current = n.id;
      shownAt.current = Date.now();
      activeAt.current = 0;
      setLeaving(false);
      setNudge(n);
      markNudgeSeen(n.id).catch(() => {});
    }).catch(() => {});
    poll();
    const t = setInterval(poll, POLL_MS);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  // Watch for activity while it's showing, and leave when both conditions are met.
  useEffect(() => {
    if (!nudge) return undefined;
    const onActive = () => {
      if (!activeAt.current) activeAt.current = Date.now();
    };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, onActive, { passive: true }));
    const t = setInterval(() => {
      if (!activeAt.current) return;
      const leaveAt = Math.max(shownAt.current + MIN_SHOW_MS, activeAt.current + AFTER_ACTIVE_MS);
      if (Date.now() >= leaveAt) close();
    }, 500);
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, onActive));
      clearInterval(t);
    };
  }, [nudge, close]);

  if (!nudge) return null;

  return (
    <div className={`nudge-toast${leaving ? ' leaving' : ''}`} role="alert" aria-live="assertive">
      <div className="nudge-toast-head">
        <span className="nudge-flag" aria-hidden="true" />
        <h2>Pit stop</h2>
        <button type="button" className="nudge-close" aria-label="Close" onClick={close}>
          <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>
      <p className="nudge-message">{nudge.message}</p>
    </div>
  );
};

export default NudgeToast;
